'use client';

import { HStack } from '@terra/ui-styled-system';
import { Badge } from '../../components/badge';
import { Text } from '../../components/text';
import { Tooltip } from '../../components/tooltip';
import {
  getCweLabel,
  getOwaspCategoryLabel,
} from '@terra/shared-schemas-orchestrator';

type SecurityClassificationBadgesProps = {
  cweIds?: string[] | null;
  owaspCategory?: string | null;
  size?: 'sm' | 'md';
  density?: 'default' | 'compact';
  align?: 'start' | 'end';
  maxCweBadges?: number;
};

export function SecurityClassificationBadges({
  cweIds,
  owaspCategory,
  size = 'sm',
  density = 'default',
  align = 'start',
  maxCweBadges = density === 'compact' ? 2 : 6,
}: SecurityClassificationBadgesProps) {
  const normalizedCweIds = (cweIds ?? []).filter(Boolean);
  const hasOwasp = Boolean(owaspCategory);

  if (normalizedCweIds.length === 0 && !hasOwasp) {
    return null;
  }

  const fontSize = size === 'md' ? 'sm' : 'xs';
  const visibleCweIds = normalizedCweIds.slice(0, maxCweBadges);
  const hiddenCweCount = normalizedCweIds.length - visibleCweIds.length;
  const primaryCweId = visibleCweIds[0];
  const extraCweIds = visibleCweIds.slice(1);
  const owaspLabel =
    hasOwasp && owaspCategory ? getOwaspCategoryLabel(owaspCategory) : null;

  const badgeParts = [
    ...(primaryCweId ? [primaryCweId] : []),
    ...(owaspCategory ? [owaspCategory] : []),
  ];
  const badgeLabel = badgeParts.join(' / ');

  const tooltipParts = [
    ...(primaryCweId ? [`${primaryCweId}: ${getCweLabel(primaryCweId)}`] : []),
    ...extraCweIds.map((cweId) => `${cweId}: ${getCweLabel(cweId)}`),
    ...(hiddenCweCount > 0
      ? normalizedCweIds
          .slice(maxCweBadges)
          .map((cweId) => `${cweId}: ${getCweLabel(cweId)}`)
      : []),
    ...(owaspCategory
      ? [`${owaspCategory}: ${owaspLabel ?? owaspCategory}`]
      : []),
  ];

  return (
    <HStack
      gap={1}
      flexWrap="wrap"
      w="fit-content"
      maxW="full"
      alignItems="center"
      justifyContent={align === 'end' ? 'flex-end' : 'flex-start'}
    >
      <Tooltip title={tooltipParts.join('\n')} whiteSpace="pre-line">
        <Badge variant="outline" colorScheme="terra.9">
          <Text fontSize={fontSize} fontFamily="mono" whiteSpace="nowrap">
            {badgeLabel}
            {hiddenCweCount > 0 || extraCweIds.length > 0
              ? ` +${hiddenCweCount + extraCweIds.length}`
              : ''}
          </Text>
        </Badge>
      </Tooltip>
    </HStack>
  );
}
